import { splitNamespace } from 'utils/templateVariableParsing';
import {
  hasDisplayableVariableValue,
  hasJobVariableValues,
} from 'utils/variableValuePrimitives';
import { lookupTemplateVariableValue } from 'utils/variableValueLookup';

/**
 * The resolved value shown when hovering a job variable token, or null when the job has no
 * displayable value for it (the caller shows its own "not found" copy).
 */
export const getJobVariableTooltipLabel = (
  variableName: string,
  variableValues: Record<string, string> | null | undefined
): string | null => {
  const value = lookupTemplateVariableValue(variableName, variableValues);
  return hasDisplayableVariableValue(value) ? value : null;
};

/** What a deprecated variable token's tooltip shows; the caller words the notice. */
export interface DeprecatedVariableTooltip {
  /** The substitution preview, or null when nothing displayable resolves. */
  value: string | null;
  /** The canonical variable name (no braces), or null when none is known. */
  replacement: string | null;
}

/** Substitution preview and canonical replacement for a deprecated variable token. */
export const getDeprecatedVariableTooltip = (
  variableName: string,
  replacementPattern?: string,
  variableValues?: Record<string, string> | null
): DeprecatedVariableTooltip => {
  const replacement = replacementPattern || null;
  const value =
    lookupTemplateVariableValue(variableName, variableValues) ??
    (replacement
      ? lookupTemplateVariableValue(replacement, variableValues)
      : undefined);
  return {
    value: hasDisplayableVariableValue(value) ? value : null,
    replacement,
  };
};

/**
 * Names that the catalog RECOGNIZES but which resolve to nothing on THIS job, so their placeholder
 * will render blank here. Advisory only — never an error and never an activation blocker.
 *
 * The variable catalog is ORG-scoped, so a recognized `{{job.x}}` no longer implies "populated on
 * this job"; without this the only surviving emptiness signal is a hover tooltip. A name counts as
 * blank-risk when it resolves through the job map to a missing/whitespace-only value, OR when it is
 * a known job-attribute path the loaded job simply does not carry.
 *
 * Three guards keep it from firing spuriously: an unloaded catalog returns the empty set (the job
 * map arrives on its own request, so without this an empty-string value would flag before we know
 * the name is recognized), an empty/null `jobVariableValues` (org-level editors, templates,
 * still-loading) returns the empty set, and only `job.*` / bare job-attribute names are ever
 * considered — org variables, built-ins, and send/call-stage references are left alone.
 */
export const blankOnThisJobVariableNames = (
  variableNames: Iterable<string>,
  jobVariableValues: Record<string, string> | null | undefined,
  jobAttributeNames: ReadonlySet<string> = new Set<string>(),
  catalogLoaded = true
): ReadonlySet<string> => {
  const blankNames = new Set<string>();
  if (!catalogLoaded || !hasJobVariableValues(jobVariableValues)) {
    return blankNames;
  }
  for (const name of variableNames) {
    const split = splitNamespace(name);
    // Only config-stage job references are judged here. A send/call namespace has no value yet by
    // design, and a bare name must be a known job-attribute path (not an org var or built-in).
    if (split === null) {
      if (!jobAttributeNames.has(name)) {
        continue;
      }
    } else if (split.prefix !== 'job') {
      continue;
    }
    const value = lookupTemplateVariableValue(name, jobVariableValues);
    // Absent from the loaded job map counts too: the org catalog offers paths this job lacks.
    const isKnownJobPath =
      split === null ? true : jobAttributeNames.has(split.tail);
    if (
      value === undefined ? isKnownJobPath : !hasDisplayableVariableValue(value)
    ) {
      blankNames.add(name);
    }
  }
  return blankNames;
};
